import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Center, Spinner } from '@chakra-ui/react';
import { cardSlugPath } from '../utils/cardUtils';
import type { RiftboundCard } from '../types';

/**
 * Handles old-style `/cards/:legacyId` URLs (opaque Riftcodex IDs).
 * Fetches the card by ID, derives the human-readable slug, and 301-replaces
 * the URL with `/cards/{setCode}/{number}-{name}`.
 * Falls back to `/search` if the card cannot be found.
 */
export function LegacyCardRedirect() {
  const { legacyId } = useParams<{ legacyId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!legacyId) {
      navigate('/search', { replace: true });
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/card?id=${encodeURIComponent(legacyId)}`);
        if (!res.ok || cancelled) throw new Error('not found');
        const card = (await res.json()) as RiftboundCard;
        if (cancelled) return;
        if (card?.id && card.setCode && card.number) {
          navigate(`/cards/${cardSlugPath(card)}`, { replace: true });
        } else {
          navigate('/search', { replace: true });
        }
      } catch {
        if (!cancelled) navigate('/search', { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [legacyId, navigate]);

  return (
    <Center py={20}>
      <Spinner color="accent" />
    </Center>
  );
}
